import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../adminApi';
import AdminLayout from './AdminLayout';

const fmt = (n) => Number(n || 0).toLocaleString();
const fmtDate = (val) => {
    if (!val) return '—';
    try { return new Date(val).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }); }
    catch { return val; }
};

const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

const accountTypeLabel = (t) => t === 'savings' ? '普通' : t === 'checking' ? '当座' : t || '—';

const BankInfoModal = ({ payout, onClose }) => {
    const p = payout;
    const rows = [
        ['銀行名', p.bank_name ? `${p.bank_name}（${p.bank_code || '—'}）` : '—'],
        ['支店名', p.branch_name ? `${p.branch_name}（${p.branch_code || '—'}）` : '—'],
        ['口座種別', accountTypeLabel(p.account_type)],
        ['口座番号', p.account_number || '—'],
        ['口座名義', p.account_holder || '—'],
    ];
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', minWidth: 340, maxWidth: 480, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                <h3 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 700, color: '#1a1a2e' }}>振込先口座情報</h3>
                <p style={{ margin: '0 0 20px', fontSize: '0.82rem', color: '#6b7280' }}>
                    {p.contractor?.company_name || `業者 #${p.contractor_id}`}　振込金額：¥{fmt(p.net_amount)}
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <tbody>
                        {rows.map(([label, value]) => (
                            <tr key={label} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '9px 0', color: '#6b7280', width: 90, fontWeight: 500 }}>{label}</td>
                                <td style={{ padding: '9px 0', color: '#1a1a2e', fontWeight: label === '口座番号' ? 600 : 400 }}>{value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <button onClick={onClose} style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #e0e4ea', background: '#fff', cursor: 'pointer', fontSize: '0.875rem', color: '#374151' }}>
                        閉じる
                    </button>
                </div>
            </div>
        </div>
    );
};

const AdminPayoutPage = () => {
    const navigate = useNavigate();
    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [completing, setCompleting] = useState(null);
    const [bankModal, setBankModal] = useState(null);

    const load = () => {
        setLoading(true);
        api.get('admin/payouts?status=all')
            .then(r => setPayouts(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const handleComplete = async (payout) => {
        const name = payout.contractor?.company_name || '業者';
        const amount = fmt(payout.net_amount);
        const bank = payout.bank_name ? `${payout.bank_name} ${payout.branch_name || ''} ${accountTypeLabel(payout.account_type)} ${payout.account_number || ''}`.trim() : '（口座情報未登録）';
        if (!window.confirm(`【振込完了の確認】\n\n振込先：${name}\n振込先口座：${bank}\n振込金額：¥${amount}\n\n銀行振込が完了していることを確認のうえ、「OK」を押してください。\nステータスが「振込済み」に更新されます。`)) return;
        setCompleting(payout.id);
        try {
            await api.post(`admin/payouts/${payout.id}/complete`);
            load();
        } catch {
            alert('処理に失敗しました');
        } finally {
            setCompleting(null);
        }
    };

    const filtered = payouts.filter(p => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
            String(p.id).includes(s) ||
            String(p.project_id).includes(s) ||
            (p.contractor?.company_name || '').toLowerCase().includes(s)
        );
    });

    const pendingPayouts = payouts.filter(p => ['manual', 'pending', 'failed'].includes(p.status));
    const pendingTotal = pendingPayouts.reduce((sum, p) => sum + Number(p.net_amount || 0), 0);

    return (
        <AdminLayout>
            <div style={{ padding: '32px' }}>

                {/* ヘッダー */}
                <div style={{ marginBottom: 24 }}>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>振込管理</h2>
                    <p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: '0.875rem' }}>業者への振込を管理</p>
                </div>

                {/* サマリカード */}
                <div style={{
                    marginBottom: 24,
                    background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
                    borderRadius: 8, padding: '24px 28px', color: '#fff',
                }}>
                    <p style={{ fontSize: '0.83rem', opacity: 0.9, margin: '0 0 6px' }}>振込待ち合計金額</p>
                    <p style={{ fontSize: '1.8rem', fontWeight: 700, margin: '0 0 6px' }}>¥{fmt(pendingTotal)}</p>
                    <p style={{ fontSize: '0.8rem', opacity: 0.75, margin: 0 }}>{pendingPayouts.length}件の振込待ち</p>
                </div>

                {/* 検索バー */}
                <div style={{ marginBottom: 20, maxWidth: 400, position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }}>
                        <SearchIcon />
                    </span>
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="業者名、案件ID、振込IDで検索"
                        style={{
                            width: '100%', paddingLeft: 40, paddingRight: 16, paddingTop: 9, paddingBottom: 9,
                            border: '1px solid #e0e4ea', borderRadius: 4, fontSize: '0.875rem',
                            outline: 'none', boxSizing: 'border-box',
                        }}
                    />
                </div>

                {/* テーブル */}
                <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e0e4ea' }}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                                <thead>
                                    <tr style={{ background: '#f5f6f8', borderBottom: '1px solid #e0e4ea' }}>
                                        {['振込ID', '案件ID', '業者名', '手数料', '振込金額', '振込期日', 'ステータス', '操作'].map(h => (
                                            <th key={h} style={{ padding: '10px 20px', textAlign: 'left', color: '#6b7280', fontWeight: 500, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                                                該当する振込依頼がありません
                                            </td>
                                        </tr>
                                    ) : filtered.map(p => {
                                        const isPending = ['manual', 'pending', 'failed'].includes(p.status);
                                        const dueDate = p.invoice?.due_date || p.created_at;
                                        return (
                                            <tr key={p.id} style={{ borderBottom: '1px solid #e0e4ea' }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f5f6f8'}
                                                onMouseLeave={e => e.currentTarget.style.background = ''}>
                                                <td style={{ padding: '14px 20px', color: '#1a1a2e', fontWeight: 500, whiteSpace: 'nowrap' }}>#{p.id}</td>
                                                <td style={{ padding: '14px 20px', whiteSpace: 'nowrap' }}>
                                                    <button
                                                        onClick={() => navigate(`/admin/projects/${p.project_id}`)}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#005c53', fontWeight: 500, fontSize: '0.875rem', padding: 0 }}
                                                    >
                                                        #{p.project_id}
                                                    </button>
                                                </td>
                                                <td style={{ padding: '14px 20px', color: '#1a1a2e', whiteSpace: 'nowrap' }}>
                                                    {p.contractor?.company_name || `業者 #${p.contractor_id}`}
                                                </td>
                                                <td style={{ padding: '14px 20px', color: '#6b7280', whiteSpace: 'nowrap' }}>¥{fmt(p.platform_fee)}</td>
                                                <td style={{ padding: '14px 20px', color: '#1a1a2e', fontWeight: 600, whiteSpace: 'nowrap' }}>¥{fmt(p.net_amount)}</td>
                                                <td style={{ padding: '14px 20px', color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(dueDate)}</td>
                                                <td style={{ padding: '14px 20px', whiteSpace: 'nowrap' }}>
                                                    {isPending ? (
                                                        <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 500, background: '#fef9c3', color: '#a16207' }}>振込待ち</span>
                                                    ) : (
                                                        <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 500, background: '#dcfce7', color: '#15803d' }}>振込済み</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '14px 20px', whiteSpace: 'nowrap' }}>
                                                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                                        <button
                                                            onClick={() => setBankModal(p)}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#005c53', fontWeight: 500, fontSize: '0.875rem', padding: 0, textDecoration: 'underline' }}
                                                        >
                                                            振込情報を確認
                                                        </button>
                                                        {isPending ? (
                                                            <button
                                                                onClick={() => handleComplete(p)}
                                                                disabled={completing === p.id}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981', fontWeight: 600, fontSize: '0.875rem', padding: 0 }}
                                                            >
                                                                {completing === p.id ? '処理中...' : '振込を完了'}
                                                            </button>
                                                        ) : (
                                                            <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>完了済み</span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        {bankModal && <BankInfoModal payout={bankModal} onClose={() => setBankModal(null)} />}
        </AdminLayout>
    );
};

export default AdminPayoutPage;
